import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { apiErrorCode } from '../../lib/apiError'
import { notify } from '../../lib/notifications'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Input } from '../ui/input'

const NOTIFICATION_KEY = 'impersonation_link'

/**
 * The « Copy impersonation link » action (spec 2026-10-04-03), shared by the
 * usage drawer and the contact page. It creates a single-use link and copies
 * its URL: the admin opens it in another browser or a private window, since
 * the session lives in localStorage, shared by every tab of this browser.
 * When the clipboard API refuses, the URL shows in a dialog with a read-only
 * field to select by hand.
 *
 * Returns `copy`, the pending state, whether the last copy succeeded (for an
 * inline confirmation where the global notification is covered, as in a
 * sheet) and the fallback dialog, which the caller renders.
 */
export function useCopyImpersonationLink() {
    const { t } = useTranslation('admin')
    const create = $api.useMutation('post', '/api/v1/admin/users/{userUid}/impersonation-links')
    const [fallbackUrl, setFallbackUrl] = useState<string | null>(null)
    const [copied, setCopied] = useState(false)
    const [errorKey, setErrorKey] = useState<string | null>(null)

    const copy = async (userUid: string, organizationCode?: string) => {
        setCopied(false)
        setErrorKey(null)
        let url: string
        try {
            const link = await create.mutateAsync({
                params: { path: { userUid } },
                body: organizationCode ? { organization_code: organizationCode } : {},
            })
            url = link.url
        } catch (err) {
            const key = apiErrorCode(err) === 'impersonation_forbidden'
                ? 'impersonation.error_forbidden'
                : 'impersonation.error'
            setErrorKey(key)
            notify({ key: NOTIFICATION_KEY, variant: 'destructive', titleKey: `admin:${key}` })
            return
        }
        try {
            await navigator.clipboard.writeText(url)
        } catch {
            setFallbackUrl(url)
            return
        }
        setCopied(true)
        notify({
            key: NOTIFICATION_KEY,
            variant: 'default',
            titleKey: 'admin:impersonation.copied_title',
            descriptionKey: 'admin:impersonation.copied_description',
        })
    }

    const dialog = (
        <Dialog open={fallbackUrl !== null} onOpenChange={(open) => { if (!open) setFallbackUrl(null) }}>
            <DialogContent data-testid="impersonation-link-dialog">
                <DialogHeader>
                    <DialogTitle>{t('impersonation.dialog_title')}</DialogTitle>
                    <DialogDescription>{t('impersonation.copied_description')}</DialogDescription>
                </DialogHeader>
                <Input
                    readOnly
                    value={fallbackUrl ?? ''}
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label={t('impersonation.dialog_title')}
                    data-testid="impersonation-link-url"
                />
            </DialogContent>
        </Dialog>
    )

    return { copy, isPending: create.isPending, copied, errorKey, dialog }
}
